/**
 * Import wizard step 2 — capture raw URLs/text, CSV upload, or trigger URL extraction for remote sources.
 *
 * Where: `ImportLeadsModal` after method selection.
 * Uses: parent callbacks for file/text and `onExtractUrls` for async scraping.
 * Behavior: shows extraction spinner/errors from parent props.
 */

import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Input,
} from '@mui/material';
import { CloudUpload as CloudUploadIcon, Close as CloseIcon } from '@mui/icons-material';
import { ImportMethod } from './ImportLeadsModalStep1';

interface Step2InputDataProps {
  method: ImportMethod | null;
  text?: string;
  file?: File;
  urlCount: number;
  isExtracting: boolean;
  extractionError: Error | null;
  onTextChange: (text: string) => void;
  onFileChange: (file: File) => void;
  onFileRemove: () => void;
  onExtractUrls: () => void;
}

export const Step2InputData: React.FC<Step2InputDataProps> = ({
  method,
  text,
  file,
  urlCount,
  isExtracting,
  extractionError,
  onTextChange,
  onFileChange,
  onFileRemove,
  onExtractUrls,
}) => {
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0];
    if (uploadedFile) {
      // Check file size in frontend
      const MAX_FILE_SIZE = 100000; // 100 KB - should match backend config
      if (uploadedFile.size > MAX_FILE_SIZE) {
        alert(`File size (${(uploadedFile.size / 1024).toFixed(1)} KB) exceeds maximum allowed size of ${MAX_FILE_SIZE / 1024} KB`);
        return;
      }
      onFileChange(uploadedFile);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
      {method === 'text' && (
        <>
          <TextField
            label="Paste LinkedIn URLs"
            multiline
            minRows={10}
            fullWidth
            value={text || ''}
            onChange={(e) => onTextChange(e.target.value)}
            placeholder="Paste LinkedIn profile URLs here, one per line..."
            disabled={isExtracting}
          />
          <Button
            variant="contained"
            onClick={onExtractUrls}
            disabled={!text || !text.trim() || isExtracting}
            startIcon={isExtracting ? <CircularProgress size={20} /> : undefined}
          >
            {isExtracting ? 'Extracting URLs...' : 'Extract URLs'}
          </Button>
        </>
      )}
      {method === 'csv' && (
        <>
          <Box
            sx={{
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main' },
            }}
            onClick={() => document.getElementById('csv-file-input')?.click()}
          >
            <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6">Upload CSV File</Typography>
            <Typography variant="body2" color="text.secondary">
              Click to upload or drag and drop
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Maximum file size: 100 KB
            </Typography>
            <Input
              id="csv-file-input"
              type="file"
              inputProps={{ accept: '.csv' }}
              sx={{ display: 'none' }}
              onChange={handleFileUpload}
            />
          </Box>
          {file && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">{file.name}</Typography>
              <IconButton size="small" onClick={onFileRemove}>
                <CloseIcon />
              </IconButton>
            </Box>
          )}
          {isExtracting && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Extracting URLs...</Typography>
            </Box>
          )}
        </>
      )}
      {extractionError && (
        <Alert severity="error">
          {extractionError.message || 'Failed to extract URLs'}
        </Alert>
      )}
      {urlCount > 0 && (
        <Alert severity="success">
          Found {urlCount} valid LinkedIn URL{urlCount !== 1 ? 's' : ''}
        </Alert>
      )}
    </Box>
  );
};

